import { Key, MoreHorizontal, Tag, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BadgeOverflow } from "@/components/common/BadgeOverflow";
import { DataTable, badgeCapacity } from "@/components/common/DataTable";
import { UserIdentityCell } from "@/components/users/UserIdentityCell";

export interface CompanyUserRow {
  id: string;
  email: string;
  full_name?: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  roles: { sys: string[]; custom: string[]; isSuper: boolean };
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString() : "Never");

/** Shared Notion-style roster used by /settings/users and /platform/companies/:id/users. */
export function CompanyUsersTable({
  storageKey,
  users,
  loading,
  canRemoveSuper,
  resettingUserId,
  onAssignRoles,
  onSendReset,
  onRemove,
  empty,
}: {
  storageKey: string;
  users: CompanyUserRow[];
  loading?: boolean;
  canRemoveSuper: boolean;
  resettingUserId: string | null;
  onAssignRoles: (id: string) => void;
  onSendReset: (id: string, email: string) => void;
  onRemove: (u: { id: string; email: string }) => void;
  empty?: string;
}) {
  const companyRole = (u: CompanyUserRow) => (u.roles.isSuper ? "superadmin" : u.roles.sys[0] ?? "user");
  return (
    <DataTable<CompanyUserRow>
      storageKey={storageKey}
      rows={users}
      rowKey={(u) => u.id}
      loading={loading}
      empty={empty}
      defaultSort={{ key: "email", asc: true }}
      columns={[
        {
          key: "email",
          header: "User",
          width: 280,
          sortValue: (u) => (u.full_name || u.email).toLowerCase(),
          cell: (u) => <UserIdentityCell id={u.id} name={u.full_name} email={u.email} />,
        },
        {
          key: "role",
          header: "Company role",
          width: 140,
          sortValue: (u) => ({ superadmin: 0, admin: 1, user: 2 } as Record<string, number>)[companyRole(u)] ?? 3,
          cell: (u) => (
            <Badge variant={companyRole(u) === "user" ? "outline" : "default"} className="whitespace-nowrap">
              {companyRole(u)}
            </Badge>
          ),
        },
        {
          key: "custom",
          header: "Custom roles",
          width: 260,
          sortValue: (u) => u.roles.custom.length,
          cell: (u, { width }) => (
            <BadgeOverflow
              visibleCount={badgeCapacity(width)}
              items={u.roles.custom.map((r) => ({ id: r, label: r }))}
            />
          ),
        },
        {
          key: "created_at",
          header: "Registered",
          width: 125,
          sortValue: (u) => u.created_at,
          cell: (u) => <span className="text-sm text-muted-foreground">{fmt(u.created_at)}</span>,
        },
        {
          key: "last_sign_in_at",
          header: "Last sign-in",
          width: 125,
          sortValue: (u) => u.last_sign_in_at ?? "",
          cell: (u) => <span className="text-sm text-muted-foreground">{fmt(u.last_sign_in_at)}</span>,
        },
      ]}
      actions={(u) => {
        const blocked = u.roles.isSuper && !canRemoveSuper;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Actions for ${u.email}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAssignRoles(u.id)}>
                <Tag className="w-4 h-4 mr-2" /> Assign roles
              </DropdownMenuItem>
              <DropdownMenuItem disabled={resettingUserId === u.id} onClick={() => onSendReset(u.id, u.email)}>
                <Key className="w-4 h-4 mr-2" />
                {resettingUserId === u.id ? "Sending…" : "Send reset email"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={blocked}
                className="text-destructive focus:text-destructive"
                onClick={() => onRemove({ id: u.id, email: u.email })}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {blocked ? "Only a superadmin can remove" : "Remove from company"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }}
    />
  );
}
